/**
 * src/lib/contentSegments.ts
 *
 * Splits a Markdown string into an ordered sequence of typed segments:
 *   - 'markdown'  → plain Markdown rendered by react-markdown
 *   - 'quiz'      → MicroQuiz block (```microquiz … ```)
 *   - 'checklist' → InteractiveChecklist block (```checklist … ```)
 *
 * Supports multiple embedded blocks of any mix per article.
 */

import { parseQuizFromString, type MicroQuizData } from './microQuiz';
import { parseChecklist, type ChecklistData } from './interactiveChecklist';

export type ContentSegment =
  | { type: 'markdown'; content: string }
  | { type: 'quiz'; data: MicroQuizData }
  | { type: 'checklist'; data: ChecklistData; anchorIndex: number };

/**
 * Regex matches fenced blocks for our custom block types.
 * Must be reset (lastIndex = 0) before every call due to the /g flag.
 */
const BLOCK_REGEX = /```(microquiz|checklist)\s*([\s\S]*?)```/gi;

export function extractContentSegments(markdown: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  let lastIndex = 0;
  let checklistCount = 0;

  BLOCK_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = BLOCK_REGEX.exec(markdown)) !== null) {
    const blockStart = match.index;
    const blockEnd = blockStart + match[0].length;

    // Content before this block
    const before = markdown.slice(lastIndex, blockStart).trim();
    if (before) segments.push({ type: 'markdown', content: before });

    const blockType = match[1].toLowerCase();
    const rawContent = (match[2] ?? '').trim();

    if (blockType === 'microquiz' && rawContent) {
      const quiz = parseQuizFromString(rawContent);
      if (quiz) segments.push({ type: 'quiz', data: quiz });
    } else if (blockType === 'checklist' && rawContent) {
      const checklist = parseChecklist(rawContent);
      if (checklist) {
        segments.push({ type: 'checklist', data: checklist, anchorIndex: checklistCount });
        checklistCount += 1;
      }
    }

    lastIndex = blockEnd;
  }

  // Remaining markdown after the last block
  const remaining = markdown.slice(lastIndex).trim();
  if (remaining) segments.push({ type: 'markdown', content: remaining });

  // If no blocks were found, return the full content as a single markdown segment
  if (segments.length === 0 && markdown.trim()) {
    return [{ type: 'markdown', content: markdown }];
  }

  return segments;
}

